import React, { useEffect } from 'react';
import RewardProgressBar from '../../../components/common/RewardProgressBar';
import ClockIcon from './icons/ClockIcon';

// Helper: determine if a hex color is light
const isLightColor = (hexColor) => {
    if (!hexColor) return false;
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 140;
};

// Helper: darken a hex color by a percentage
const darkenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max((num >> 16) - amt, 0);
    const G = Math.max((num >> 8 & 0x00FF) - amt, 0);
    const B = Math.max((num & 0x0000FF) - amt, 0);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

// ── Progress Bar component ──────────────────────────────────────
const ProgressBar = ({ value, color, label, subLabel }) => (
    <div className="mb-3 last:mb-0">
        <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{label}</span>
            {subLabel && <span className="text-xs text-gray-400 dark:text-gray-500">{subLabel}</span>}
        </div>
        <div className="w-full h-2.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
            <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{ width: `${Math.min(Math.max(value, 0), 100)}%`, backgroundColor: color }}
            />
        </div>
    </div>
);

// ── Metric Card component ───────────────────────────────────────
const MetricCard = ({ label, value, suffix = '%', sub, color }) => (
    <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-3 border border-gray-100 dark:border-white/5">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">{label}</p>
        <div className="flex items-baseline gap-0.5">
            <span className="text-2xl font-black text-gray-900 dark:text-white">{value}</span>
            {suffix && <span className="text-xs font-bold text-gray-400 dark:text-gray-500">{suffix}</span>}
        </div>
        {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
        {color && (
            <div className="w-full h-1 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden mt-2">
                <div className="h-full rounded-full" style={{ width: `${Math.min(Math.max(value, 0), 100)}%`, backgroundColor: color }} />
            </div>
        )}
    </div>
);

// ── Context Pill (shows "Exams" or "Cours") ─────────────────────
const ContextPill = ({ activeTab, language }) => {
    const isExam = activeTab !== 'courses';
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
            isExam
                ? 'bg-white/20 text-white/90'
                : 'bg-white/20 text-white/90'
        }`}>
            {isExam
                ? (language === 'fr' ? 'Examens' : 'Exams')
                : (language === 'fr' ? 'Cours' : 'Courses')
            }
        </span>
    );
};

// ── Icons per type ──────────────────────────────────────────────
const BadgeIcon = ({ type, className }) => {
    switch (type) {
        case 'days':
            return <ClockIcon className={className} />;
        case 'progression':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            );
        case 'comprehension':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            );
        case 'rewards':
            return <img src="/img/application/Victoire.png" alt="" className={`${className} brightness-0 dark:invert`} style={{ objectFit: 'contain' }} />;
        default:
            return null;
    }
};

// ── Title per type ──────────────────────────────────────────────
const getTitle = (type, language) => {
    const titles = {
        days: { fr: 'Jours restants', en: 'Days Remaining' },
        progression: { fr: 'Progression', en: 'Progression' },
        comprehension: { fr: 'Maîtrise', en: 'Mastery' },
        rewards: { fr: 'Récompenses', en: 'Rewards' },
    };
    return titles[type]?.[language] || titles[type]?.fr || '';
};

// ── Contextual types ────────────────────────────────────────────
const CONTEXTUAL_TYPES = ['progression', 'comprehension'];

// ═════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════

const StatBadgeDetailModal = ({ type, statsDetail, activeTab, levelColor, language, levelId, onClose }) => {
    const isLight = isLightColor(levelColor);
    const isContextual = CONTEXTUAL_TYPES.includes(type);
    const effectiveTab = activeTab === 'stats' ? 'exams' : activeTab;

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // ── DAYS REMAINING ──────────────────────────────────────────
    const renderDaysContent = () => {
        if (levelId === 'pstar') {
            return (
                <div className="text-center py-4">
                    <p className="text-6xl font-black text-gray-900 dark:text-white mb-4">∞</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {language === 'fr'
                            ? 'Accès gratuit et illimité — aucune date d\'expiration.'
                            : 'Free and unlimited access — no expiration date.'}
                    </p>
                </div>
            );
        }

        const hasCourseSub = statsDetail?.hasCourseAvailable;
        const daysExam = statsDetail?.daysRemainingExam ?? 0;
        const daysCourse = statsDetail?.daysRemainingCourse ?? 0;
        const daysTotal = statsDetail?.daysRemaining ?? 0;

        return (
            <>
                {hasCourseSub ? (
                    <div className="space-y-4">
                        <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 border border-gray-100 dark:border-white/5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                    {language === 'fr' ? 'Abonnement Examens' : 'Exam Subscription'}
                                </span>
                                <span className="text-2xl font-black text-gray-900 dark:text-white">{daysExam}<span className="text-xs font-bold text-gray-400 ml-1">{language === 'fr' ? 'jours' : 'days'}</span></span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((daysExam / 270) * 100, 100)}%`, backgroundColor: levelColor }} />
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 border border-gray-100 dark:border-white/5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                    {language === 'fr' ? 'Abonnement Cours' : 'Course Subscription'}
                                </span>
                                <span className="text-2xl font-black text-gray-900 dark:text-white">{daysCourse}<span className="text-xs font-bold text-gray-400 ml-1">{language === 'fr' ? 'jours' : 'days'}</span></span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((daysCourse / 270) * 100, 100)}%`, backgroundColor: levelColor }} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-2">
                        <p className="text-5xl font-black text-gray-900 dark:text-white">{daysTotal}</p>
                        <p className="text-sm font-bold text-gray-400 dark:text-gray-500 mt-1">{language === 'fr' ? 'jours' : 'days'}</p>
                    </div>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 leading-relaxed">
                    {language === 'fr'
                        ? 'Nombre de jours restants sur votre abonnement actif. Après expiration, votre progression est conservée mais l\'accès au contenu est suspendu.'
                        : 'Number of days remaining on your active subscription. After expiration, your progress is kept but access to content is suspended.'}
                </p>
            </>
        );
    };

    // ── PROGRESSION ─────────────────────────────────────────────
    const renderProgressionContent = () => {
        const isExamContext = effectiveTab !== 'courses';
        const value = isExamContext ? (statsDetail?.progressExamen ?? 0) : (statsDetail?.progressCourse ?? 0);
        const completedCourses = statsDetail?.completedCourses;
        const totalCourses = statsDetail?.totalCourses;
        const validatedChapters = statsDetail?.validatedChapters;
        const totalChapters = statsDetail?.totalChapters;
        const hasCourseCount = !isExamContext && completedCourses != null && totalCourses != null;
        const hasChapterCount = !isExamContext && validatedChapters != null && totalChapters != null && totalChapters > 0;
        const chapterPercent = hasChapterCount ? Math.round((validatedChapters / totalChapters) * 100) : 0;

        return (
            <>
                <div className="text-center py-2 mb-4">
                    <p className="text-5xl font-black text-gray-900 dark:text-white">{value}<span className="text-xl font-bold text-gray-400 dark:text-gray-500">%</span></p>
                    {hasCourseCount && (
                        <p className="text-sm font-bold text-gray-400 dark:text-gray-500 mt-1">
                            {completedCourses} / {totalCourses} {language === 'fr' ? 'chapitres' : 'chapters'}
                        </p>
                    )}
                </div>
                <ProgressBar
                    value={value}
                    color={levelColor}
                    label={isExamContext
                        ? (language === 'fr' ? 'Examens terminés' : 'Exams completed')
                        : (language === 'fr' ? 'Chapitres terminés' : 'Chapters completed')
                    }
                    subLabel={hasCourseCount ? `${completedCourses}/${totalCourses}` : undefined}
                />
                {hasChapterCount && (
                    <ProgressBar
                        value={chapterPercent}
                        color={levelColor}
                        label={language === 'fr' ? 'Sections validées' : 'Sections validated'}
                        subLabel={`${validatedChapters}/${totalChapters}`}
                    />
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 leading-relaxed">
                    {isExamContext
                        ? (language === 'fr'
                            ? 'Pourcentage d\'examens que vous avez complétés au moins une fois dans cette catégorie. Essayez de couvrir l\'ensemble des sujets pour une préparation optimale.'
                            : 'Percentage of exams you have completed at least once in this category. Try to cover all topics for optimal preparation.')
                        : (language === 'fr'
                            ? 'Un chapitre est complété quand toutes ses sections ont été lues. Terminez l\'ensemble des chapitres pour maîtriser toutes les notions.'
                            : 'A chapter is completed when all its sections have been read. Finish all chapters to master every concept.')
                    }
                </p>
            </>
        );
    };

    // ── COMPREHENSION ───────────────────────────────────────────
    const renderComprehensionContent = () => {
        const isExamContext = effectiveTab !== 'courses';

        if (isExamContext) {
            const mastery = statsDetail?.comprehensionExamen ?? 0;
            const masteryDisplay = mastery === 0 && (statsDetail?.correctCount ?? 0) > 0 ? '< 1' : mastery;
            const successRate = statsDetail?.successRate ?? 0;
            const lastExamRate = (statsDetail?.lastSessionTotal ?? 0) > 0 ? statsDetail.lastSessionRate : null;

            return (
                <>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        <MetricCard
                            label={language === 'fr' ? 'Compréhension' : 'Comprehension'}
                            value={masteryDisplay}
                            sub={`${statsDetail?.correctCount ?? 0}/${statsDetail?.totalQuestions ?? 0}`}
                            color={levelColor}
                        />
                        <MetricCard
                            label={language === 'fr' ? 'Réussite' : 'Success'}
                            value={successRate}
                            sub={`${statsDetail?.totalCorrectAnswers ?? 0}/${statsDetail?.totalAnswersGiven ?? 0}`}
                            color={levelColor}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-2 mb-4">
                        <MetricCard
                            label={language === 'fr' ? 'Dernière' : 'Last'}
                            value={lastExamRate ?? '—'}
                            suffix={lastExamRate !== null ? '%' : ''}
                            sub={lastExamRate !== null ? `${statsDetail?.lastSessionCorrect ?? 0}/${statsDetail?.lastSessionTotal ?? 0}` : null}
                            color={lastExamRate !== null ? levelColor : undefined}
                        />
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed space-y-2">
                        <p>
                            <span className="font-bold text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Compréhension' : 'Comprehension'}</span>
                            <br />
                            {language === 'fr'
                                ? 'Taux de bonnes réponses pondéré sur l\'ensemble des questions uniques.'
                                : 'Weighted correct answer rate across all unique questions.'}
                        </p>
                        <p>
                            <span className="font-bold text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Réussite' : 'Success'}</span>
                            <br />
                            {language === 'fr'
                                ? 'Ratio brut de bonnes réponses sur toutes vos tentatives.'
                                : 'Raw ratio of correct answers across all attempts.'}
                        </p>
                        <p>
                            <span className="font-bold text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Dernière' : 'Last'}</span>
                            <br />
                            {language === 'fr'
                                ? 'Résultats les plus récents agrégés sur chaque examen.'
                                : 'Most recent results aggregated across each exam.'}
                        </p>
                    </div>
                </>
            );
        }

        // Courses context
        const courseComprehension = statsDetail?.comprehensionCourse ?? 0;
        const quizCount = statsDetail?.courseQuizCount ?? 0;
        const bestScore = statsDetail?.courseQuizBestScore ?? 0;
        const lastQuizRate = (statsDetail?.courseQuizLastSessionTotal ?? 0) > 0 ? statsDetail.courseQuizLastSessionRate : null;

        if (quizCount === 0) {
            return (
                <>
                    <div className="text-center py-6">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {language === 'fr'
                                ? 'Complétez des quiz de cours pour voir vos statistiques de compréhension.'
                                : 'Complete course quizzes to see your comprehension statistics.'}
                        </p>
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="grid grid-cols-1 gap-2 mb-4">
                    <MetricCard
                        label={language === 'fr' ? 'Moyenne' : 'Average'}
                        value={courseComprehension}
                        sub={`${quizCount} quiz`}
                        color={levelColor}
                    />
                    <MetricCard
                        label={language === 'fr' ? 'Meilleur' : 'Best'}
                        value={bestScore}
                        color={levelColor}
                    />
                    <MetricCard
                        label={language === 'fr' ? 'Dernière' : 'Last'}
                        value={lastQuizRate ?? '—'}
                        suffix={lastQuizRate !== null ? '%' : ''}
                        sub={lastQuizRate !== null ? `${statsDetail?.courseQuizLastSessionCorrect ?? 0}/${statsDetail?.courseQuizLastSessionTotal ?? 0}` : null}
                        color={lastQuizRate !== null ? levelColor : undefined}
                    />
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed space-y-2">
                    <p>
                        <span className="font-bold text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Moyenne' : 'Average'}</span>
                        <br />
                        {language === 'fr'
                            ? 'Score moyen obtenu sur l\'ensemble de vos quiz de cours.'
                            : 'Average score across all your course quizzes.'}
                    </p>
                    <p>
                        <span className="font-bold text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Meilleur' : 'Best'}</span>
                        <br />
                        {language === 'fr'
                            ? 'Votre meilleur score, reflétant votre pic de performance.'
                            : 'Your best score, reflecting your peak performance.'}
                    </p>
                    <p>
                        <span className="font-bold text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Dernière' : 'Last'}</span>
                        <br />
                        {language === 'fr'
                            ? 'Résultats les plus récents agrégés sur chaque quiz.'
                            : 'Most recent results aggregated across each quiz.'}
                    </p>
                </div>
            </>
        );
    };

    // ── REWARDS ─────────────────────────────────────────────────
    const renderRewardsContent = () => {
        const badges = statsDetail?.badges ?? [];
        const count = statsDetail?.rewards ?? 0;
        const total = statsDetail?.rewardsTotal ?? 10;

        const formatProgress = (badge) => {
            return `${Math.round(badge.progress)}/${badge.target}`;
        };

        return (
            <>
                <div className="text-center py-2 mb-3">
                    <p className="text-5xl font-black text-gray-900 dark:text-white">{count}<span className="text-xl font-bold text-gray-400 dark:text-gray-500">/{total}</span></p>
                </div>

                {/* Progress bar toward rewards */}
                {statsDetail?.rewardTiers?.length > 0 && (
                    <div className="mb-3 px-1">
                        <RewardProgressBar
                            count={count}
                            total={total}
                            rewardTiers={statsDetail.rewardTiers}
                            color={levelColor}
                            size="md"
                            variant="card"
                        />
                    </div>
                )}

                {/* Badge list */}
                <div className="space-y-2 max-h-[45vh] overflow-y-auto custom-scrollbar">
                    {badges.map((badge) => (
                        <div
                            key={badge.key}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                badge.unlocked
                                    ? 'bg-white dark:bg-white/5 border-gray-100 dark:border-white/10'
                                    : 'bg-gray-50 dark:bg-white/[0.02] border-gray-100 dark:border-white/5 opacity-50'
                            }`}
                        >
                            {/* Badge icon */}
                            <div
                                className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                    badge.unlocked ? '' : 'bg-gray-100 dark:bg-white/10'
                                }`}
                                style={badge.unlocked ? { backgroundColor: `${levelColor}15` } : undefined}
                            >
                                <img
                                    src={`/img/application/rewards/${badge.key}.png`}
                                    alt=""
                                    className={`w-full h-full object-contain ${badge.unlocked ? '' : 'grayscale opacity-40'}`}
                                />
                            </div>

                            {/* Badge info */}
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-bold truncate ${badge.unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                                    {language === 'fr' ? badge.labelFr : badge.labelEn}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                    {language === 'fr' ? badge.descFr : badge.descEn}
                                </p>
                                {/* Progress bar for locked badges with target > 1 */}
                                {!badge.unlocked && badge.target > 1 && (
                                    <div className="w-full h-1.5 bg-gray-100 dark:bg-white/10 rounded-full mt-1.5 overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all"
                                            style={{
                                                width: `${Math.min((badge.progress / badge.target) * 100, 100)}%`,
                                                backgroundColor: levelColor
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Status */}
                            <div className="flex-shrink-0">
                                {badge.unlocked ? (
                                    <div
                                        className="w-6 h-6 rounded-full flex items-center justify-center"
                                        style={{ backgroundColor: `${levelColor}20` }}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: levelColor }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                ) : (
                                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                        {formatProgress(badge)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Achievement codes (multi-tier) */}
                {(statsDetail?.achievementCodes?.length > 0) && (
                    <div className="mt-4 space-y-2">
                        {statsDetail.achievementCodes.map((ac, i) => (
                            <div key={i} className="p-3 bg-green-50 dark:bg-green-500/10 rounded-xl border border-green-200 dark:border-green-500/20 text-center">
                                <p className="text-sm font-bold text-green-700 dark:text-green-400 mb-1">
                                    {language === 'fr'
                                        ? `${ac.badgeThreshold ? `${ac.badgeThreshold}/${total} badges` : 'Récompense'} — ${ac.percentage} % de rabais :`
                                        : `${ac.badgeThreshold ? `${ac.badgeThreshold}/${total} badges` : 'Reward'} — ${ac.percentage}% off:`}
                                </p>
                                <p className="text-lg font-black tracking-wider text-green-800 dark:text-green-300 select-all">
                                    {ac.code}
                                </p>
                                {ac.expiresAt && (
                                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                                        {language === 'fr' ? 'Expire le' : 'Expires'} {new Date(ac.expiresAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* All badges unlocked message (no codes) */}
                {count === total && total > 0 && !(statsDetail?.achievementCodes?.length > 0) && (
                    <div className="mt-4 p-3 bg-green-50 dark:bg-green-500/10 rounded-xl border border-green-200 dark:border-green-500/20 text-center">
                        <p className="text-sm font-bold text-green-700 dark:text-green-400">
                            {language === 'fr'
                                ? 'Tous les badges debloqués !'
                                : 'All badges unlocked!'}
                        </p>
                    </div>
                )}

                {count < total && !(statsDetail?.achievementCodes?.length > 0) && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mt-3">
                        {language === 'fr'
                            ? 'Débloquez des badges pour obtenir des récompenses.'
                            : 'Unlock badges to earn rewards.'}
                    </p>
                )}
            </>
        );
    };

    // ── Render body switch ──────────────────────────────────────
    const renderContent = () => {
        switch (type) {
            case 'days': return renderDaysContent();
            case 'progression': return renderProgressionContent();
            case 'comprehension': return renderComprehensionContent();
            case 'rewards': return renderRewardsContent();
            default: return null;
        }
    };

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
                style={{ animation: 'badgeModalIn 0.25s ease-out' }}
            >
                {/* Header with level color */}
                <div
                    className="p-5 pb-4 relative overflow-hidden"
                    style={{
                        background: `linear-gradient(135deg, ${levelColor}, ${darkenColor(levelColor, 20)})`
                    }}
                >
                    <div className="flex justify-between items-start relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/15 rounded-xl">
                                <BadgeIcon type={type} className={`w-5 h-5 ${isLight ? 'text-gray-900' : 'text-white'}`} />
                            </div>
                            <div>
                                <h3 className={`text-xl font-black ${isLight ? 'text-gray-900' : 'text-white'}`}>
                                    {getTitle(type, language)}
                                </h3>
                                {isContextual && (
                                    <div className="mt-1">
                                        <ContextPill activeTab={effectiveTab} language={language} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-full transition ${isLight ? 'bg-black/10 hover:bg-black/20' : 'bg-white/10 hover:bg-white/20'}`}
                        >
                            <svg className={`w-4 h-4 ${isLight ? 'text-gray-900' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-5">
                    {renderContent()}
                </div>
            </div>

            {/* Animation keyframes */}
            <style>{`
                @keyframes badgeModalIn {
                    from { opacity: 0; transform: scale(0.92) translateY(10px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default StatBadgeDetailModal;
